<template>
  <div>
    <div class="form-section group">
      <span class="icon">
        <icon-colored-weather />
      </span>
      {{ weatherTitle }}
      <div
        v-if="canBeRemoved"
        class="actions">
        <button
          class="btn btn-danger btn-sm"
          :disabled="isReadOnly"
          @click="clear">
          <icon-delete />
          {{ actionRemoveTitle }}
        </button>
      </div>
    </div>
    <transition name="fade">
      <div
        v-if="isMounted"
        class="form-section-content">
        <div class="form-group row">
          <div
            :id="formLabelId"
            class="col-sm-12">
            <weather-targeting-editor
              :wrapped-in-pane="false"
              :is-read-only="isReadOnly"
              :entity="entity"
              :is-line-item-weather-targeting="true"
              :targeting-criteria="targetingCriteria"
              :selected-weather-conditions="weatherTargetingConditions"
              :temperature-code="weatherTargetingTemperatureCode"
              :temperature-value="weatherTargetingTemperatureValue"
              :selected-uv-conditions="uvTargetingConditions"
              :selected-destination-weather-conditions="destinationWeatherTargetingConditions"
              :destination-latitude="destinationLatitude"
              :destination-longitude="destinationLongitude"
              :destination-temperature-code="destinationWeatherTargetingTemperatureCode"
              :destination-temperature-value="destinationWeatherTargetingTemperatureValue"
              :destination-snow-accumulation-code="destinationSnowAccumulationCode"
              :destination-snow-accumulation-value="destinationSnowAccumulationValue"
              :destination-rain-accumulation-code="destinationRainAccumulationCode"
              :destination-rain-accumulation-value="destinationRainAccumulationValue"
              :enable-u-v-targeting="hasAccessToUVTargeting"
              :enable-destination-weather="hasAccessToDestinationWeather"
              @selected-weather-conditions-changed="selectedWeatherConditionsChanged"
              @temperature-code-changed="temperatureCodeChanged"
              @temperature-value-changed="temperatureValueChanged"
              @selected-u-v-conditions-changed="selectedUVConditionsChanged"
              @selected-destination-weather-conditions-changed="destinationWeatherTargetingConditionsChanged"
              @destination-latitude-changed="destinationLatitudeChanged"
              @destination-longitude-changed="destinationLongitudeChanged"
              @destination-temperature-code-changed="destinationTemperatureCodeChanged"
              @destination-temperature-value-changed="destinationWeatherTargetingTemperatureValueChanged"
              @destination-snow-accumulation-code-changed="destinationSnowAccumulationCodeChanged"
              @destination-snow-accumulation-value-changed="destinationSnowAccumulationValueChanged"
              @destination-rain-accumulation-code-changed="destinationRainAccumulationCodeChanged"
              @destination-rain-accumulation-value-changed="destinationRainAccumulationValueChanged"
              @cleared-local-targeting="clearLocalTargeting"
              @cleared-destination-targeting="clearDestinationTargeting" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, onMounted, onUnmounted, ref } from 'vue';

  import WeatherTargetingEditor from '@/common/components/controls/targeting/weather/WeatherTargetingEditor.vue';
  import EntityFormHelper from '@/common/components/entity-form/EntityFormHelper';
  import { Criterion } from '@/common/components/entity-form/EntityType';
  import TargetingSectionHelper, {
    ITargetingCriteria,
    TargetingCode,
  } from '@/common/components/entity-form/TargetingSectionHelper';
  import TargetingCriteriaType from '@/common/enums/TargetingCriteriaType.enum';
  import IconColoredWeather from '@/common/icons/colored/Weather.vue';
  import IconDelete from '@/common/icons/monochrome/Delete.vue';
  import { LineItem, Package, Plan } from '@/common/models';
  import i18n from '@/vueI18n';

  interface IProps {
    entity: LineItem | Plan | Package;
    canBeRemoved: boolean;
    isReadOnly: boolean;
  }

  const emit = defineEmits(['cleared']);

  const props = withDefaults(defineProps<IProps>(), {
    canBeRemoved: false,
    isReadOnly: false,
  });

  const isMounted = ref(false);
  const internalDestinationLongitude = ref<number | null>(null);
  const internalDestinationLatitude = ref<number | null>(null);

  const actionRemoveTitle = computed<string>(() => capitalize(i18n.global.t('actions.remove')));
  const destinationCoordinates = computed<string>(() => {
    if (locationBasedCriterionWithLocation.value == null) {
      return null;
    }

    const locationCriterionValue = [...locationBasedCriterionWithLocation.value.values].find(
      (v) => v.code === TargetingCode.Location,
    );

    return locationCriterionValue.value;
  });
  const destinationCriteria = computed<ITargetingCriteria[]>(() =>
    targetingCriteria.value.filter(
      (x) =>
        x.type === TargetingCriteriaType.DestinationRain ||
        x.type === TargetingCriteriaType.DestinationSnow ||
        x.type === TargetingCriteriaType.DestinationTemperature ||
        x.type === TargetingCriteriaType.DestinationWeather,
    ),
  );
  const destinationCriteriaIDs = computed<number[]>(() => destinationCriteria.value.map((x) => x.id));
  const destinationLatitude = computed<number>({
    get() {
      if (
        !internalDestinationLatitude.value &&
        targetingCriteria.value?.length > 0 &&
        destinationCriteria.value?.length > 0 &&
        entityTargetingCriteria.value?.length > 0
      ) {
        return destinationCoordinates.value ? Number.parseFloat(destinationCoordinates.value.split(',')[0]) : null;
      }

      return internalDestinationLatitude.value;
    },
    set(val: number) {
      internalDestinationLatitude.value = val;
    },
  });
  const destinationLongitude = computed<number>({
    get() {
      if (
        !internalDestinationLongitude.value &&
        targetingCriteria.value?.length > 0 &&
        destinationCriteria.value?.length &&
        entityTargetingCriteria.value?.length > 0
      ) {
        return destinationCoordinates.value ? Number.parseFloat(destinationCoordinates.value.split(',')[1]) : null;
      }
      return null;
    },
    set(val: number) {
      internalDestinationLongitude.value = val;
    },
  });
  const destinationRainAccumulationCode = computed({
    get() {
      const rainCriterion = targetingCriteria.value?.find((x) => x.type === TargetingCriteriaType.DestinationRain);
      if (rainCriterion && entityTargetingCriteria.value) {
        const criterion: Criterion = entityTargetingCriteria.value.find(
          (x) => x.targetingCriterionID === rainCriterion.id,
        );
        return getCode(criterion);
      }

      return null;
    },
    set(val) {
      const rainTargetingCriteria = getDestinationRainTargetingCriteria(
        val,
        val ? destinationRainAccumulationValue.value : null,
      );
      const otherTargetingCriterion = targetingCriteria.value.filter(
        (x) => x.type !== TargetingCriteriaType.DestinationRain,
      );

      updateEntityCriteria([
        ...(rainTargetingCriteria == null ? [] : rainTargetingCriteria),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });
  const destinationRainAccumulationValue = computed({
    get() {
      const rainCriterion = targetingCriteria.value?.find((x) => x.type === TargetingCriteriaType.DestinationRain);
      if (rainCriterion && entityTargetingCriteria.value) {
        const criterion: Criterion = entityTargetingCriteria.value.find(
          (x) => x.targetingCriterionID === rainCriterion.id,
        );
        return getValue(criterion) ? parseFloat(getValue(criterion)) : null;
      }

      return null;
    },
    set(val) {
      const rainTargetingCriteria = getDestinationRainTargetingCriteria(
        val ? destinationRainAccumulationCode.value : null,
        val,
      );
      const otherTargetingCriterion = targetingCriteria.value.filter(
        (x) => x.type !== TargetingCriteriaType.DestinationRain,
      );
      updateEntityCriteria([
        ...(rainTargetingCriteria == null ? [] : rainTargetingCriteria),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });
  const destinationSnowAccumulationCode = computed({
    get() {
      const snowCriterion = targetingCriteria.value?.find((x) => x.type === TargetingCriteriaType.DestinationSnow);
      if (snowCriterion && entityTargetingCriteria.value) {
        const criterion: Criterion = entityTargetingCriteria.value.find(
          (x) => x.targetingCriterionID === snowCriterion.id,
        );
        return getCode(criterion);
      }
      return null;
    },
    set(val) {
      const snowTargetingCriteria = getDestinationSnowTargetingCriteria(
        val,
        val ? destinationSnowAccumulationValue.value : null,
      );
      const otherTargetingCriterion = targetingCriteria.value.filter(
        (x) => x.type !== TargetingCriteriaType.DestinationSnow,
      );
      updateEntityCriteria([
        ...(snowTargetingCriteria == null ? [] : snowTargetingCriteria),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });
  const destinationSnowAccumulationValue = computed({
    get() {
      const snowCriterion = targetingCriteria.value?.find((x) => x.type === TargetingCriteriaType.DestinationSnow);
      if (snowCriterion && entityTargetingCriteria.value) {
        const criterion: Criterion = entityTargetingCriteria.value.find(
          (x) => x.targetingCriterionID === snowCriterion.id,
        );
        return getValue(criterion) ? parseFloat(getValue(criterion)) : null;
      }

      return null;
    },
    set(val) {
      const snowTargetingCriteria = getDestinationSnowTargetingCriteria(
        val ? destinationSnowAccumulationCode.value : null,
        val,
      );
      const otherTargetingCriterion = targetingCriteria.value.filter(
        (x) => x.type !== TargetingCriteriaType.DestinationSnow,
      );
      updateEntityCriteria([
        ...(snowTargetingCriteria == null ? [] : snowTargetingCriteria),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });
  const destinationWeatherTargetingConditions = computed({
    get() {
      const destinationWeatherCriterion = targetingCriteria.value.find(
        (x) => x.type === TargetingCriteriaType.DestinationWeather,
      );
      if (destinationWeatherCriterion && entityTargetingCriteria.value) {
        const criterion: Criterion = entityTargetingCriteria.value.find(
          (x) => x.targetingCriterionID === destinationWeatherCriterion.id,
        );

        return getValue(criterion)?.split(',') ?? null;
      }

      return null;
    },
    set(val) {
      const destinationWeatherTargetingCriteria = getWeatherTargetingCriteria(
        val,
        destinationWeatherTargetingTemperatureCode.value,
        destinationWeatherTargetingTemperatureValue.value,
        false,
      );
      const otherTargetingCriterion = targetingCriteria.value.filter(
        (x) =>
          x.type !== TargetingCriteriaType.DestinationTemperature &&
          x.type !== TargetingCriteriaType.DestinationWeather,
      );
      updateEntityCriteria([
        ...(destinationWeatherTargetingCriteria == null ? [] : destinationWeatherTargetingCriteria),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });
  const destinationWeatherTargetingTemperatureCode = computed({
    get() {
      const temperatureCriterion = targetingCriteria.value?.find(
        (x) => x.type === TargetingCriteriaType.DestinationTemperature,
      );
      if (temperatureCriterion && entityTargetingCriteria.value) {
        const criterion: Criterion = entityTargetingCriteria.value.find(
          (x) => x.targetingCriterionID === temperatureCriterion.id,
        );
        return getCode(criterion);
      }

      return null;
    },
    set(val) {
      const destinationWeatherTargetingCriteria = getWeatherTargetingCriteria(
        destinationWeatherTargetingConditions.value,
        val,
        val ? destinationWeatherTargetingTemperatureValue.value : null,
        false,
      );
      const otherTargetingCriterion = targetingCriteria.value.filter(
        (x) =>
          x.type !== TargetingCriteriaType.DestinationTemperature &&
          x.type !== TargetingCriteriaType.DestinationWeather,
      );
      updateEntityCriteria([
        ...(destinationWeatherTargetingCriteria == null ? [] : destinationWeatherTargetingCriteria),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });
  const destinationWeatherTargetingTemperatureValue = computed({
    get() {
      const temperatureCriterion = targetingCriteria.value?.find(
        (x) => x.type === TargetingCriteriaType.DestinationTemperature,
      );
      if (temperatureCriterion && entityTargetingCriteria.value) {
        const criterion: Criterion = entityTargetingCriteria.value.find(
          (x) => x.targetingCriterionID === temperatureCriterion.id,
        );

        return getValue(criterion) ? parseFloat(getValue(criterion)) : null;
      }

      return null;
    },
    set(val) {
      const destinationWeatherTargetingCriteria = getWeatherTargetingCriteria(
        destinationWeatherTargetingConditions.value,
        val ? destinationWeatherTargetingTemperatureCode.value : null,
        val,
        false,
      );
      const otherTargetingCriterion = targetingCriteria.value.filter(
        (x) =>
          x.type !== TargetingCriteriaType.DestinationTemperature &&
          x.type !== TargetingCriteriaType.DestinationWeather,
      );
      updateEntityCriteria([
        ...(destinationWeatherTargetingCriteria == null ? [] : destinationWeatherTargetingCriteria),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });
  const entityFormHelper = computed<EntityFormHelper>(() => new EntityFormHelper(props.entity));
  const entityTargetingCriteria = computed<Criterion[]>(() => props.entity.targetingCriteria || []);
  const formLabelId = computed<string>(() => entityFormHelper.value.getFormLabelId('edit', 'weather'));
  const hasAccessToUVTargeting = computed<boolean>(
    () => targetingCriteria.value?.some((x) => x.type === TargetingCriteriaType.UV) ?? false,
  );
  const hasAccessToDestinationWeather = computed<boolean>(
    () => !!(isLineItem.value && !(props.entity as LineItem).packageID) || !!(isPlan.value && !isPackage.value),
  );
  const isPackage = computed<boolean>(() => entityFormHelper.value.isPackage);
  const isPlan = computed<boolean>(() => entityFormHelper.value.isPlan);
  const isLineItem = computed<boolean>(() => entityFormHelper.value.isLineItem);
  const locationBasedCriterionWithLocation = computed<Criterion | null>(() =>
    entityTargetingCriteria.value.find(
      (x) =>
        destinationCriteriaIDs.value.includes(x.targetingCriterionID) &&
        x.values.some((v) => v.code === TargetingCode.Location && v.value != null),
    ),
  );
  const targetingSectionHelper = computed<TargetingSectionHelper>(() => new TargetingSectionHelper(props.entity));
  const targetingCriteria = computed<ITargetingCriteria[]>(() => targetingSectionHelper.value.targetingCriteria);
  const weatherTitle = computed<string>(() => capitalize(i18n.global.t('messages.weatherTargeting')));
  const weatherTargetingTemperatureCode = computed<string>({
    get() {
      const temperatureCriterion = targetingCriteria.value?.find((x) => x.type === TargetingCriteriaType.Temperature);
      if (temperatureCriterion && entityTargetingCriteria.value) {
        const criterion: Criterion = entityTargetingCriteria.value.find(
          (x) => x.targetingCriterionID === temperatureCriterion.id,
        );
        return criterion?.values[0].code ?? null;
      }

      return null;
    },
    set(val) {
      const weatherTargetingCriteria = getWeatherTargetingCriteria(
        weatherTargetingConditions.value,
        val,
        val ? weatherTargetingTemperatureValue.value : null,
      );
      const otherTargetingCriterion = targetingCriteria.value.filter(
        (x) => x.type !== TargetingCriteriaType.Temperature && x.type !== TargetingCriteriaType.Weather,
      );
      updateEntityCriteria([
        ...(weatherTargetingCriteria == null ? [] : weatherTargetingCriteria),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });
  const weatherTargetingTemperatureValue = computed<number>({
    get() {
      const temperatureCriterion = targetingCriteria.value?.find((x) => x.type === TargetingCriteriaType.Temperature);
      if (temperatureCriterion && entityTargetingCriteria.value) {
        const criterion: Criterion = entityTargetingCriteria.value.find(
          (x) => x.targetingCriterionID === temperatureCriterion.id,
        );
        return getValue(criterion) ? parseFloat(getValue(criterion)) : null;
      }

      return null;
    },
    set(val) {
      const weatherTargetingCriteria = getWeatherTargetingCriteria(
        weatherTargetingConditions.value,
        val ? weatherTargetingTemperatureCode.value : null,
        val,
      );
      const otherTargetingCriterion = targetingCriteria.value.filter(
        (x) => x.type !== TargetingCriteriaType.Temperature && x.type !== TargetingCriteriaType.Weather,
      );
      updateEntityCriteria([
        ...(weatherTargetingCriteria || []),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });
  const weatherTargetingConditions = computed({
    get() {
      const weatherCriterion = targetingCriteria.value.find((x) => x.type === TargetingCriteriaType.Weather);

      if (weatherCriterion && entityTargetingCriteria.value) {
        const criterion: Criterion = entityTargetingCriteria.value.find(
          (x) => x.targetingCriterionID === weatherCriterion.id,
        );
        return getValue(criterion)?.split(',') ?? null;
      }

      return null;
    },
    set(val) {
      const weatherTargetingCriteria = getWeatherTargetingCriteria(
        val,
        weatherTargetingTemperatureCode.value,
        weatherTargetingTemperatureValue.value,
      );
      const otherTargetingCriterion = targetingCriteria.value.filter(
        (x) => x.type !== TargetingCriteriaType.Temperature && x.type !== TargetingCriteriaType.Weather,
      );

      updateEntityCriteria([
        ...(weatherTargetingCriteria == null ? [] : weatherTargetingCriteria),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });
  const uvTargetingConditions = computed({
    get() {
      const uvCriterion = targetingCriteria.value.find((x) => x.type === TargetingCriteriaType.UV);
      if (uvCriterion && targetingCriteria.value.find((x) => x.type === TargetingCriteriaType.UV)) {
        const criterion: Criterion = entityTargetingCriteria.value.find(
          (x) => x.targetingCriterionID === uvCriterion.id,
        );
        return getValue(criterion)?.split(',') ?? null;
      }

      return null;
    },
    set(val) {
      const uvTargetingCriteria = getUVTargetingCriteria(val);
      const otherTargetingCriterion = targetingCriteria.value.filter((x) => x.type !== TargetingCriteriaType.UV);
      updateEntityCriteria([
        ...(uvTargetingCriteria == null ? [] : uvTargetingCriteria),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });

  function updateEntityCriteria(value: Criterion[]): void {
    entityFormHelper.value.updateEntityCriteria(value);
  }

  function newEntityCriteria(
    entityId: number,
    criterionId: number,
    values: { code: string; value: string }[],
  ): Criterion {
    return targetingSectionHelper.value.newEntityCriteria(entityId, criterionId, values);
  }

  function addLocationToCriterion(criterion: Criterion): void {
    if (destinationLatitude.value && destinationLongitude.value) {
      criterion.values.push({
        code: TargetingCode.Location,
        value: `${destinationLatitude.value},${destinationLongitude.value}`,
      });
    }
  }

  function getValue(criterion: Criterion | null): string | null {
    return criterion ? [...criterion.values].find((x) => x.code !== TargetingCode.Location)?.value || null : null;
  }

  function getCode(criterion: Criterion | null): string | null {
    return criterion ? [...criterion.values].find((x) => x.code !== TargetingCode.Location)?.code || null : null;
  }

  function getWeatherTargetingCriteria(
    conditions: unknown[],
    weatherTargetingTemperatureCode: string,
    weatherTargetingTemperatureValue: number,
    isLocal = true,
  ): Criterion[] | null {
    const weatherCode = isLocal ? TargetingCriteriaType.Weather : TargetingCriteriaType.DestinationWeather;
    const criteria: Criterion[] = [];

    if (conditions) {
      const weatherCriterion = targetingCriteria.value.find((x) => x.type === weatherCode);
      const criterion = newEntityCriteria(props.entity.id, weatherCriterion.id, [
        {
          code: TargetingCriteriaType.Weather,
          value: conditions.join(','),
        },
      ]);
      addLocationToCriterion(criterion);
      criteria.push(criterion);
    }

    if (weatherTargetingTemperatureCode || weatherTargetingTemperatureValue) {
      const temperatureCode = isLocal
        ? TargetingCriteriaType.Temperature
        : TargetingCriteriaType.DestinationTemperature;
      const temperatureCriterion = targetingCriteria.value.find((x) => x.type === temperatureCode);
      const criterion = newEntityCriteria(props.entity.id, temperatureCriterion.id, [
        {
          code: weatherTargetingTemperatureCode || TargetingCriteriaType.DailyMaxAbove,
          value: weatherTargetingTemperatureValue?.toString() || '20',
        },
      ]);
      addLocationToCriterion(criterion);
      criteria.push(criterion);
    }

    return criteria?.length > 0 ? criteria : null;
  }

  function getUVTargetingCriteria(conditions: unknown[]): Criterion[] | null {
    const criteria: Criterion[] = [];

    if (conditions?.length > 0) {
      const uvCriterion = targetingCriteria.value.find((x) => x.type === TargetingCriteriaType.UV);
      const criterion = newEntityCriteria(props.entity.id, uvCriterion.id, [
        {
          code: TargetingCriteriaType.UvCategory,
          value: conditions.join(','),
        },
      ]);
      addLocationToCriterion(criterion);
      criteria.push(criterion);
    }

    return criteria?.length > 0 ? criteria : null;
  }

  function getDestinationSnowTargetingCriteria(code: string, value: number): Criterion[] | null {
    const criteria: Criterion[] = [];
    const snowCriterion = targetingCriteria.value.find((x) => x.type === TargetingCriteriaType.DestinationSnow);
    if (code || value) {
      const criterion = newEntityCriteria(props.entity.id, snowCriterion.id, [
        {
          code: code != null ? code : TargetingCode.SnowCmLast12hAbove,
          value: value != null ? `${value}` : '1',
        },
      ]);
      addLocationToCriterion(criterion);
      criteria.push(criterion);
    }

    return criteria?.length > 0 ? criteria : null;
  }

  function getDestinationRainTargetingCriteria(code: string, value: number): Criterion[] | null {
    const criteria = [];
    const rainCriterion = targetingCriteria.value.find((x) => x.type === TargetingCriteriaType.DestinationRain);

    if (code || value) {
      const criterion = newEntityCriteria(props.entity.id, rainCriterion.id, [
        {
          code: code ?? TargetingCode.RainMmLast12hAbove,
          value: value != null ? `${value}` : '1',
        },
      ]);
      addLocationToCriterion(criterion);
      criteria.push(criterion);
    }

    return criteria?.length > 0 ? criteria : null;
  }

  function clearUvTargeting(): void {
    const destinationCriteriaIDs = targetingCriteria.value
      .filter((x) => x.type === TargetingCriteriaType.UV)
      .map((x) => x.id);
    const newEntityCriteria = entityTargetingCriteria.value.filter(
      (x) => !destinationCriteriaIDs.includes(x.targetingCriterionID),
    );
    updateEntityCriteria(newEntityCriteria);
  }

  function clearDestinationTargeting(): void {
    const destinationCriteriaIDs = destinationCriteria.value.map((x) => x.id);
    const newEntityCriteria = entityTargetingCriteria.value.filter(
      (x) => !destinationCriteriaIDs.includes(x.targetingCriterionID),
    );
    updateEntityCriteria(newEntityCriteria);
  }

  function clearLocalTargeting(): void {
    clearUvTargeting();
    const destinationCriteriaIDs = targetingCriteria.value
      .filter((x) => x.type === TargetingCriteriaType.Weather || x.type === TargetingCriteriaType.Temperature)
      .map((x) => x.id);
    const newEntityCriteria = entityTargetingCriteria.value.filter(
      (x) => !destinationCriteriaIDs.includes(x.targetingCriterionID),
    );
    updateEntityCriteria(newEntityCriteria);
  }

  function clear(): void {
    clearLocalTargeting();
    clearDestinationTargeting();
    emit('cleared');
  }

  function selectedWeatherConditionsChanged(x: string[]): void {
    weatherTargetingConditions.value = x;
  }

  function temperatureCodeChanged(x: string): void {
    weatherTargetingTemperatureCode.value = x;
  }

  function temperatureValueChanged(x: number): void {
    weatherTargetingTemperatureValue.value = x;
  }

  function selectedUVConditionsChanged(x: string[]): void {
    uvTargetingConditions.value = x;
  }

  function destinationWeatherTargetingConditionsChanged(x: string[]): void {
    destinationWeatherTargetingConditions.value = x;
  }

  function destinationLatitudeChanged(x: number): void {
    destinationLatitude.value = x;
  }

  function destinationLongitudeChanged(x: number): void {
    destinationLongitude.value = x;
  }

  function destinationTemperatureCodeChanged(x: string): void {
    destinationWeatherTargetingTemperatureCode.value = x;
  }

  function destinationWeatherTargetingTemperatureValueChanged(x: number): void {
    destinationWeatherTargetingTemperatureValue.value = x;
  }

  function destinationSnowAccumulationCodeChanged(x: string): void {
    destinationSnowAccumulationCode.value = x;
  }

  function destinationSnowAccumulationValueChanged(x: number): void {
    destinationSnowAccumulationValue.value = x;
  }

  function destinationRainAccumulationCodeChanged(x: string): void {
    destinationRainAccumulationCode.value = x;
  }

  function destinationRainAccumulationValueChanged(x: number): void {
    destinationRainAccumulationValue.value = x;
  }

  onMounted(() => {
    isMounted.value = true;
  });

  onUnmounted(() => {
    isMounted.value = false;
  });
</script>
